/**
 * Directory snapshot for the capture grammar: contacts + companies mapped to
 * grammar entries with typed slugs, a synthetic "me" entry so `!me` resolves
 * to the self assignee (null on the wire), and the `#` tag pool (companies /
 * pipeline leads / events / recent meetings).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    contacts, companies, leads, events, meetings, loopRecords, directoryQueryOptions,
    type RecordLink,
} from "../../lib/api";
import { SELF_ID, type Directory, type DirectoryEntry, type TagKind } from "../../lib/grammar";
import {
    COMPANIES_QUERY_KEY,
    CONTACTS_QUERY_KEY,
    LEADS_QUERY_KEY,
} from "../../lib/query-keys";

const kebab = (s: string) =>
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export function useLoopDirectory(): { directory: Directory; isLoading: boolean } {
    const contactsQuery = useQuery({
        queryKey: CONTACTS_QUERY_KEY,
        queryFn: () => contacts.listAll(),
        ...directoryQueryOptions,
    });
    const companiesQuery = useQuery({
        queryKey: COMPANIES_QUERY_KEY,
        queryFn: () => companies.listAll(),
        ...directoryQueryOptions,
    });
    const leadsQuery = useQuery({
        queryKey: LEADS_QUERY_KEY,
        queryFn: () => leads.listAll(),
        ...directoryQueryOptions,
    });
    const eventsQuery = useQuery({
        queryKey: ["directory", "events"],
        queryFn: async () => (await events.list({ limit: "100" })).data,
        ...directoryQueryOptions,
    });
    const meetingsQuery = useQuery({
        queryKey: ["directory", "meetings"],
        queryFn: async () => (await meetings.list({ limit: "100" })).data,
        ...directoryQueryOptions,
    });

    const directory = useMemo<Directory>(() => {
        const contactEntries: DirectoryEntry[] = [
            { id: SELF_ID, slug: "me", aliases: ["self"], name: "Me", kind: "contact" },
            ...(contactsQuery.data ?? [])
                .filter((c) => c.name)
                .map((c) => ({
                    id: c.id,
                    slug: kebab(c.name!),
                    aliases: c.firstName ? [kebab(c.firstName)] : undefined,
                    name: c.name!,
                    kind: "contact" as const,
                })),
        ];
        const companyEntries: DirectoryEntry[] = (companiesQuery.data ?? []).map((c) => ({
            id: c.id,
            slug: kebab(c.name),
            name: c.name,
            kind: "company" as const,
        }));
        // `#` pool — companies (accounts/clients) first so a shared name
        // resolves to the account, then pipeline leads, events, meetings.
        const targets: DirectoryEntry[] = [
            ...companyEntries,
            ...(leadsQuery.data ?? []).map((l) => ({
                id: l.id, slug: kebab(l.title), name: l.title, kind: "lead" as const,
            })),
            ...(eventsQuery.data ?? []).map((e) => ({
                id: e.id, slug: kebab(e.name), name: e.name, kind: "event" as const,
            })),
            ...(meetingsQuery.data ?? []).map((m) => ({
                id: m.id, slug: kebab(m.title), name: m.title, kind: "meeting" as const,
            })),
        ];
        return { contacts: contactEntries, companies: companyEntries, targets };
    }, [contactsQuery.data, companiesQuery.data, leadsQuery.data, eventsQuery.data, meetingsQuery.data]);

    return { directory, isLoading: contactsQuery.isLoading || companiesQuery.isLoading };
}

/** Contact/company display-name lookup for record rows. */
export function useDirectoryNames(): (contactId: string | null, companyId: string | null) => string | null {
    const { directory } = useLoopDirectory();
    return useMemo(() => {
        const byId = new Map<string, string>();
        for (const e of directory.contacts) byId.set(e.id, e.name);
        for (const e of directory.companies) byId.set(e.id, e.name);
        return (contactId: string | null, companyId: string | null) =>
            (contactId && byId.get(contactId)) ?? (companyId && byId.get(companyId)) ?? null;
    }, [directory]);
}

/** Tag-target name lookup for `#` chips. */
export function useTargetNames(): (kind: TagKind, id: string) => string | null {
    const { directory } = useLoopDirectory();
    return useMemo(() => {
        const byKey = new Map<string, string>();
        for (const e of directory.targets) byKey.set(`${e.kind}:${e.id}`, e.name);
        return (kind: TagKind, id: string) => byKey.get(`${kind}:${id}`) ?? null;
    }, [directory]);
}

/** Batched `#`-link fetch for a page's visible records → recordId → links. */
export function useRecordLinksMap(recordIds: string[]): Map<string, RecordLink[]> {
    const key = [...recordIds].sort().join(",");
    const linksQuery = useQuery({
        queryKey: ["loop", "links", key],
        queryFn: () => loopRecords.links(recordIds),
        enabled: recordIds.length > 0,
    });
    return useMemo(() => {
        const map = new Map<string, RecordLink[]>();
        if (linksQuery.data?.ok) {
            for (const link of linksQuery.data.links) {
                (map.get(link.recordId) ?? map.set(link.recordId, []).get(link.recordId)!).push(link);
            }
        }
        return map;
    }, [linksQuery.data]);
}
